import React from "react";
import { useTranslation } from "react-i18next";

import "./imageUpload.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleX, faCloudUpload } from "@fortawesome/pro-regular-svg-icons";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { IProductImage } from "redux/reducers/models/product";

type ImageUploadProps = {
  image: IProductImage[]; // For displaying image stored in the database
  onNew: (image: File) => void;
  onRemove: (imageId: number) => void;
  uploadLabel?: string | React.ReactNode;
  maxWidth?: number;
  maxHeight?: number;
  minWidth?: number;
  minHeight?: number;
  imageURL?: string | null; // For displaying new images before upload
};

export default function ImageUpload(props: ImageUploadProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  function imageUploadOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files[0];
    const url = URL.createObjectURL(file);

    if (file && file.type.startsWith("image/")) {
      const img = new Image();

      img.onload = () => {
        if (img.naturalHeight > props.maxHeight || img.naturalWidth > props.maxWidth) {
          dispatch(
            showError(`Image dimensions exceed the maximum allowed size of ${props.maxWidth}x${props.maxHeight}px.`),
          );
          return;
        }
        if (img.naturalHeight < props.minHeight || img.naturalWidth < props.minWidth) {
          dispatch(
            showError(`Image dimensions are below the minimum allowed size of ${props.minWidth}x${props.minHeight}px.`),
          );
          return;
        }

        props.onNew(file);
      };
      img.src = url;
    }
  }

  return (
    <div className="image-upload-container" style={{ position: "relative" }}>
      {props.image[0] || props.imageURL ? (
        <>
          <br />
          <div className="image-upload-preview">
            <FontAwesomeIcon
              icon={faCircleX}
              size="lg"
              onClick={() => {
                if (props.image[0] && "id" in props.image[0]) {
                  props.onRemove(props.image[0].id);
                } else {
                  props.onRemove(0); // Clear the image
                }
              }}
              style={{ cursor: "pointer", position: "absolute", top: "15px", right: "15px" }}
            />
            <img
              src={
                props.image[0] && "source" in props.image[0] ? props.image[0].source : props.imageURL // Use props.imageURL
              }
              alt={"Uploaded Preview"}
              style={{ width: "100%", height: "auto", objectFit: "cover" }}
            />
          </div>
        </>
      ) : (
        <>
          <input
            type="file"
            accept="image/*"
            onChange={imageUploadOnChange}
            placeholder=""
            style={{
              width: "100%",
              height: "100%",
              opacity: 0,
              position: "absolute",
              top: 0,
              left: 0,
              cursor: "pointer",
            }}
          />
          <div className="image-upload-header">
            <FontAwesomeIcon icon={faCloudUpload} size="lg" />
          </div>
          {(props.uploadLabel && <p>{props.uploadLabel}</p>) ?? (
            <div>
              <span className="flex flex-row" style={{ justifySelf: "center" }}>
                <div style={{ fontWeight: "bold" }}>{t("Click to upload ")}</div>
                &nbsp;
                <div style={{ fontWeight: "normal" }}>{t("or drag and drop")}</div>
              </span>
              <div style={{ fontWeight: "normal", justifySelf: "center" }}>
                SVG, PNG, JPG, or GIF
                {props.maxWidth && props.maxHeight && (
                  <span>
                    &nbsp;(max. {props.maxWidth}x{props.maxHeight}px)
                  </span>
                )}
                {props.minWidth && props.minHeight && (
                  <span>
                    &nbsp;(min. {props.minWidth}x{props.minHeight}px)
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
